//Libs
import React, {
  Fragment,
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  ColDef,
  GetQuickFilterTextParams,
  ICellRendererParams,
  ITooltipParams,
  RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

//Local
import { ExistingVehicle, Vehicle } from "../../models/vehicle";
import * as strings from "../../strings";
import SiteKeyLocationCustomFilter from "../../components/tables/SiteKeyLocationCustomFilter";
import { getAssignedToString } from "../TaskList/TaskListContainer";
import { getReadableCraftType } from "../../models/craft-types";
import { getReadableTaskType } from "../../models/task-types";
import UsersCustomFilter from "../../components/tables/UsersCustomFilter";
import SettingsIcon from "@mui/icons-material/Settings";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import HeadingOne from "../../components/HeadingOne";

interface Props {
  //DATA
  vehicleList: Vehicle[];
  addNewVehicleButton: React.ReactNode;
  userDisplayNamesMap: Record<string, string>;
  //FUNCTIONS
  getLocationTitle: (id: string) => string;
  onEditVehicle: (vehicle: ExistingVehicle) => void;
  onOpenSmartScheduling: () => void;
}

export default function VehicleListPage({
  vehicleList,
  addNewVehicleButton,
  userDisplayNamesMap,
  getLocationTitle,
  onEditVehicle,
  onOpenSmartScheduling,
}: Props) {
  return (
    <div>
      <HeadingOne>{strings.VEHICLES}</HeadingOne>
      <div className={"ag-theme-alpine my-4 flex h-[600px] flex-col pb-14"}>
        <VehicleListTable
          vehicleList={vehicleList}
          getLocationTitle={getLocationTitle}
          addNewVehicleButton={addNewVehicleButton}
          onEditVehicle={onEditVehicle}
          userDisplayNamesMap={userDisplayNamesMap}
          onOpenSmartScheduling={onOpenSmartScheduling}
        />
      </div>
    </div>
  );
}

const headerStyles =
  "px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase";

const commonColProps = {
  minWidth: 150,
  flex: 1,
  resizable: true,
  filter: true,
  sortable: true,
  headerClass: headerStyles,
};

interface VehicleListTableProps {
  vehicleList: Props["vehicleList"];
  addNewVehicleButton: Props["addNewVehicleButton"];
  userDisplayNamesMap: Props["userDisplayNamesMap"];
  getLocationTitle: Props["getLocationTitle"];
  onEditVehicle: Props["onEditVehicle"];
  onOpenSmartScheduling: () => void;
}

/* TABLE COMPONENT */
const VehicleListTable = memo(
  ({
    vehicleList,
    getLocationTitle,
    addNewVehicleButton,
    userDisplayNamesMap,
    onEditVehicle,
    onOpenSmartScheduling,
  }: VehicleListTableProps) => {
    const [gridReady, setGridReady] = useState(false);
    const gridRef = useRef<any>(null);

    const onGridReady = useCallback(() => {
      setGridReady(true);
    }, []);

    useLayoutEffect(() => {
      if (gridRef.current && gridReady) {
        gridRef.current.api?.sizeColumnsToFit();
      }
    }, [gridReady]);

    const renderSiteKeyLocation = (
      params: ICellRendererParams,
    ): JSX.Element => {
      return (
        <div className="capitalize">
          {getLocationTitle(params.data.locationID)}
        </div>
      );
    };

    const renderMultipleUID = (params: ICellRendererParams): JSX.Element => {
      return (
        <div>{getAssignedToString(userDisplayNamesMap, params.value)}</div>
      );
    };

    const renderCraftTypes = (params: ICellRendererParams): JSX.Element => {
      if (params.value.length > 0) {
        const readableCraftTypes: string[] = params.value.map(
          (craftType: number) => getReadableCraftType(craftType),
        );

        return <div>{readableCraftTypes.join(", ")}</div>;
      } else {
        return <div></div>;
      }
    };

    const renderTaskTypes = (params: ICellRendererParams): JSX.Element => {
      if (params.value.length > 0) {
        const readableTaskTypes: string[] = params.value.map(
          (taskType: number) => getReadableTaskType(taskType),
        );

        return <div>{readableTaskTypes.join(", ")}</div>;
      } else {
        return <div></div>;
      }
    };

    const columnDefs: ColDef[] = [
      {
        headerName: "Title",
        autoHeight: true,
        minWidth: 300,
        flex: 2,
        getQuickFilterText: (params: GetQuickFilterTextParams) => {
          return params.data.title + " " + params.data.description;
        },
        comparator(_valueA, _valueB, nodeA, nodeB) {
          const titleA = nodeA.data.title;
          const titleB = nodeB.data.title;
          if (titleA === titleB) return 0;
          return titleA > titleB ? 1 : -1;
        },
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <div className="my-2 flex items-center">
              <div className="flex flex-col">
                <span className="leading-2 block whitespace-normal text-base font-medium capitalize text-primary">
                  {params.data.title}
                </span>
                <span className="leading-2 block whitespace-normal text-sm text-gray-400">
                  {params.data.description}
                </span>
              </div>
            </div>
          );
        },
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
      },
      {
        field: "groupName",
        headerName: "Group Name",
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
      },
      {
        field: "locationID",
        filter: SiteKeyLocationCustomFilter,
        cellRenderer: renderSiteKeyLocation,
        headerName: "Location",
        getQuickFilterText: (params: GetQuickFilterTextParams) => {
          return getLocationTitle(params.data.locationID);
        },
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "maxTasksPerDay",
        headerName: "Max Tasks Per Day",
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
      },
      {
        field: "craftTypes",
        cellRenderer: renderCraftTypes,
        headerName: "Work Types",
        tooltipValueGetter: (params: ITooltipParams) =>
          params.valueFormatted ?? params.value,
      },
      {
        field: "taskTypes",
        cellRenderer: renderTaskTypes,
        headerName: "Task Types",
        tooltipValueGetter: (params: ITooltipParams) =>
          params.valueFormatted ?? params.value,
      },

      {
        field: "defaultSiteKeyUserID",
        filter: UsersCustomFilter,
        headerName: "Assigned To",
        cellRenderer: renderMultipleUID,
        tooltipValueGetter: (params: ITooltipParams) =>
          params.valueFormatted ?? params.value,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },

      {
        headerName: "Working Hours",
        valueGetter: (params) => {
          const start = params.data.startTime;
          const end = params.data.endTime;
          if (!start || !end) {
            return "";
          }
          return `${start} - ${end}`;
        },
        tooltipValueGetter: (params: ITooltipParams) =>
          params.valueFormatted ?? params.value,
        ...commonColProps,
      },

      {
        headerName: "Excluded from Optimization",
        field: "excludeFromOptimization",
        valueGetter: (params) =>
          params.data.excludeFromOptimization ? "Yes" : "No",
        tooltipValueGetter: (params: ITooltipParams) =>
          params.valueFormatted ?? params.value,
        ...commonColProps,
      },
    ];

    return (
      <Fragment>
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end sm:justify-end">
          <BaseButtonSecondary
            type="button"
            onClick={onOpenSmartScheduling}
            className="w-full text-primary sm:w-48"
          >
            <SettingsIcon fontSize="small" className="mr-2" />
            {strings.buttons.SMART_SCHEDULING_OPTIONS}
          </BaseButtonSecondary>
          {addNewVehicleButton}
        </div>
        <AgGridReact
          reactiveCustomComponents
          ref={gridRef}
          onGridReady={onGridReady}
          defaultColDef={commonColProps}
          className="mt-5 shadow"
          rowData={vehicleList}
          animateRows={true}
          rowSelection="single"
          rowHeight={50}
          cacheQuickFilter={true}
          columnDefs={columnDefs}
          onRowClicked={(event: RowClickedEvent) => onEditVehicle(event.data)}
        />
      </Fragment>
    );
  },
);
